import React, { useEffect, useMemo, useRef, useState } from 'react';

function estimateItemHeight(item, columnWidth) {
  const previewWidth = Number(item?.preview?.width || 0);
  const previewHeight = Number(item?.preview?.height || 0);
  const mediaRatio = previewWidth > 0 && previewHeight > 0
    ? (previewHeight / previewWidth)
    : 1.12;
  const mediaHeight = columnWidth * mediaRatio;
  const titleLength = String(item?.title || '').trim().length;
  const titleRows = Math.max(1, Math.min(3, Math.ceil(titleLength / 18)));
  const metaHeight = 50 + (titleRows * 10);

  return mediaHeight + metaHeight;
}

function getColumnCount(width, {
  minColumnWidth = 122,
  gap = 6,
  minColumns = 3,
  maxColumns = 5,
}) {
  if (!width) {
    return minColumns;
  }

  const computed = Math.floor((width + gap) / (minColumnWidth + gap));
  return Math.max(minColumns, Math.min(maxColumns, computed || minColumns));
}

function distributeItems(items, columns, columnWidth) {
  const columnItems = Array.from({ length: columns }, () => []);
  const columnHeights = Array.from({ length: columns }, () => 0);

  items.forEach(item => {
    let targetIndex = 0;

    for (let index = 1; index < columns; index += 1) {
      if (columnHeights[index] < columnHeights[targetIndex]) {
        targetIndex = index;
      }
    }

    columnItems[targetIndex].push(item);
    columnHeights[targetIndex] += estimateItemHeight(item, columnWidth);
  });

  return columnItems;
}

function HistoryMasonry({
  items,
  renderItem,
  gap = 6,
  minColumnWidth = 122,
  minColumns = 3,
  maxColumns = 5,
}) {
  const containerRef = useRef(null);
  const [layout, setLayout] = useState({
    width: 0,
    columns: minColumns,
  });

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    const updateLayout = () => {
      const width = container.offsetWidth || 0;
      const columns = getColumnCount(width, {
        minColumnWidth,
        gap,
        minColumns,
        maxColumns,
      });

      setLayout(prev => (
        prev.width === width && prev.columns === columns
          ? prev
          : { width, columns }
      ));
    };

    updateLayout();

    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [gap, maxColumns, minColumnWidth, minColumns]);

  const actualColumnWidth = useMemo(() => {
    if (!layout.width || !layout.columns) {
      return minColumnWidth;
    }

    return (layout.width - (gap * (layout.columns - 1))) / layout.columns;
  }, [gap, layout.columns, layout.width, minColumnWidth]);

  const columnItems = useMemo(
    () => distributeItems(items, layout.columns, actualColumnWidth),
    [actualColumnWidth, items, layout.columns]
  );

  return (
    <div
      ref={containerRef}
      className="history-masonry"
      style={{ gap: `${gap}px` }}
    >
      {columnItems.map((column, columnIndex) => (
        <div
          key={`history-masonry-column-${columnIndex}`}
          className="history-masonry-column"
          style={{ gap: `${gap}px` }}
        >
          {column.map(item => renderItem(item))}
        </div>
      ))}
    </div>
  );
}

export default HistoryMasonry;
